import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCopy, FiCheck, FiChevronDown, FiX, FiSearch, FiAlertCircle } from 'react-icons/fi';
import { QRCodeSVG } from 'qrcode.react';
import { useWallet } from '../context/WalletContext';
import { useChain } from '../context/ChainContext';
import { MAIN_ASSETS, POPULAR_BEP20_TOKENS, MULTI_CHAIN_CONFIG } from '../constants/tokens';
import Header from './Header';
import TokenIcon from './TokenIcon';
import { toast } from 'react-toastify';
import TokenSelector from './TokenSelector';
import TokenSelectButton from './TokenSelectButton';
import '../styles/ReceivePage.css';

// Available networks configuration
const NETWORKS = [
  { id: 'TON', name: 'TON Network', enabled: true },
  { id: 'BNB', name: 'BNB Chain', enabled: true },
  { id: 'ETH', name: 'Ethereum', enabled: false },
  { id: 'SOL', name: 'Solana', enabled: false },
  { id: 'TRX', name: 'Tron', enabled: false },
  { id: 'MATIC', name: 'Polygon', enabled: false }
];

const NetworkSelector = ({ networks, selectedNetwork, onSelect, disabled, availableNetworks }) => {
  const [isOpen, setIsOpen] = useState(false);

  const getNetworkStatus = (network) => {
    const isAvailable = availableNetworks?.includes(network.id);
    const isEnabled = network.enabled && isAvailable;
    const disabledReason = !network.enabled ? 'Coming Soon' : 
                          !isAvailable ? 'Not Available' : null;
    return { isEnabled, disabledReason };
  };

  const handleNetworkClick = (network) => {
    const { isEnabled } = getNetworkStatus(network);
    if (isEnabled) {
      onSelect(network);
      setIsOpen(false);
    }
  };

  return (
    <div className="network-dropdown">
      <button 
        className={`network-dropdown-button ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        type="button"
        disabled={disabled}
      >
        <div className="selected-network">
          <span className="network-name">{selectedNetwork?.name || 'Select Network'}</span>
          {selectedNetwork?.enabled === false && (
            <span className="coming-soon-badge">Coming Soon</span>
          )}
        </div>
        <FiChevronDown className={`${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen && !disabled && (
        <>
          <div 
            className="network-dropdown-overlay"
            onClick={() => setIsOpen(false)}
          />
          <div className="network-dropdown-content">
            {networks.map(network => {
              const { isEnabled, disabledReason } = getNetworkStatus(network);
              
              return (
                <button
                  key={network.id}
                  className={`network-option ${!isEnabled ? 'disabled' : ''} ${selectedNetwork?.id === network.id ? 'selected' : ''}`}
                  onClick={() => handleNetworkClick(network)}
                  type="button"
                  disabled={!isEnabled}
                >
                  <div className="network-option-content">
                    <span className="network-name">{network.name}</span>
                    {!isEnabled && (
                      <span className="network-status-badge">{disabledReason}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const ReceivePage = () => {
  const { chainWallets, isWalletLoading, tokenBalances, customTokens, chainBalances } = useWallet();
  const { selectedChain, isTestnet, switchChain } = useChain();
  const [selectedToken, setSelectedToken] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [isTokenSelectOpen, setIsTokenSelectOpen] = useState(false);
  const [copiedText, setCopiedText] = useState('');
  const [availableTokens, setAvailableTokens] = useState({});
  const [isAddressLoading, setIsAddressLoading] = useState(true);

  // Initialize available tokens
  useEffect(() => {
    const tokens = {};
    const network = isTestnet ? 'testnet' : 'mainnet';

    // Add native tokens (TON and BNB)
    tokens.TON = {
      ...MAIN_ASSETS.TON,
      isNative: true,
      chain: 'TON',
      address: chainWallets.TON?.address || '',
      receiveAddress: chainWallets.TON?.address || '',
      balance: chainBalances.TON || '0',
      category: 'Native Tokens'
    };

    tokens.BNB = {
      ...MAIN_ASSETS.BNB,
      isNative: true,
      chain: 'BNB',
      address: chainWallets.BNB?.address || '',
      receiveAddress: chainWallets.BNB?.address || '',
      balance: chainBalances.BNB || '0',
      category: 'Native Tokens'
    };

    // Add multi-chain tokens
    Object.entries(MULTI_CHAIN_CONFIG).forEach(([symbol, config]) => {
      tokens[symbol] = {
        ...MAIN_ASSETS[symbol], // <-- merge in isMultiChain, supportedChains, etc.
        symbol,
        name: config.name || symbol,
        category: 'Multi-Chain Tokens',
        supportedChains: Object.keys(config),
        isMultiChain: true,
        chains: Object.entries(config).reduce((acc, [chainId, chainConfig]) => {
          acc[chainId] = {
            address: chainConfig[`${network}Address`],
            receiveAddress: chainId === 'TON' ? chainWallets.TON?.address : chainWallets.BNB?.address
          };
          return acc;
        }, {}),
        balance: tokenBalances[symbol]?.data?.balance || '0'
      };
    });

    // Add BEP20 tokens
    POPULAR_BEP20_TOKENS[network].forEach(token => {
      if (token.symbol === 'USDT' || token.symbol === 'USDC') return; // skip, handled as multi-chain
      tokens[token.symbol] = {
        ...token,
        category: 'BEP20 Tokens',
        chain: 'BNB',
        address: token.address,
        receiveAddress: chainWallets.BNB?.address || '',
        balance: tokenBalances[token.address]?.data?.balance || '0'
      };
    });

    // Add custom tokens
    Object.entries(customTokens).forEach(([address, token]) => {
      tokens[token.symbol] = {
        ...token,
        category: 'Custom Tokens',
        address,
        receiveAddress: chainWallets[token.chain]?.address || '',
        balance: tokenBalances[address]?.data?.balance || '0'
      };
    });

    setAvailableTokens(tokens);
    setIsAddressLoading(false);

    // Set default token based on the current chain
    if (!selectedToken) {
      const defaultToken = selectedChain === 'BNB' ? tokens.BNB : tokens.TON;
      if (defaultToken) {
        setSelectedToken(defaultToken);
        setSelectedNetwork(NETWORKS.find(n => n.id === defaultToken.chain));
      }
    }
  }, [chainWallets, isTestnet, chainBalances, tokenBalances, customTokens, selectedChain, selectedToken]);

  // Ensure selectedNetwork is always in sync with selectedToken
  useEffect(() => {
    if (!selectedToken) return;
    const supportedNetworks = getTokenSupportedNetworks(selectedToken);
    if (selectedToken.isMultiChain && supportedNetworks.length > 0) {
      // If the current selectedNetwork is still valid, keep it
      const stillValid = supportedNetworks.some(n => n === selectedNetwork?.id);
      const defaultNetwork = stillValid
        ? NETWORKS.find(n => n.id === selectedNetwork.id)
        : NETWORKS.find(n => n.id === supportedNetworks[0]);
      setSelectedNetwork(defaultNetwork);
    } else {
      setSelectedNetwork(NETWORKS.find(n => n.id === selectedToken.chain));
    }
  }, [selectedToken]);

  // Get supported networks for a token
  const getTokenSupportedNetworks = (token) => {
    if (!token) return [];
    if (token.isNative) return [token.chain];
    if (token.isMultiChain && token.supportedChains) {
      return token.supportedChains.filter(chainId => {
        const network = NETWORKS.find(n => n.id === chainId);
        return network && network.enabled;
      });
    }
    return [token.chain];
  };

  // Get token address for the selected network
  const getTokenAddress = (token, network) => {
    if (!token || !network) return '';
    
    const networkType = isTestnet ? 'testnet' : 'mainnet';
    
    if (token.isMultiChain && MULTI_CHAIN_CONFIG[token.symbol]) {
      const chainConfig = MULTI_CHAIN_CONFIG[token.symbol][network.id];
      return chainConfig ? chainConfig[`${networkType}Address`] : '';
    }
    
    if (token.isNative) {
      return network.id === 'TON' ? chainWallets.TON?.address : chainWallets.BNB?.address;
    }
    
    return token[`${networkType}Address`] || token.address || '';
  };

  const handleTokenSelect = async (token) => {
    setSelectedToken(token);
    const supportedNetworks = getTokenSupportedNetworks(token);
    // For multi-chain tokens, set the first supported network as default
    if (token.isMultiChain && supportedNetworks.length > 0) {
      // If the current selectedNetwork is still valid, keep it
      const stillValid = supportedNetworks.some(n => n === selectedNetwork?.id);
      const defaultNetwork = stillValid
        ? NETWORKS.find(n => n.id === selectedNetwork.id)
        : NETWORKS.find(n => n.id === supportedNetworks[0]);
      setSelectedNetwork(defaultNetwork);
    } else {
      // For single-chain tokens, set their native network
      setSelectedNetwork(NETWORKS.find(n => n.id === token.chain));
    }
    setIsTokenSelectOpen(false);
  };

  const handleNetworkSelect = async (network) => {
    if (!network.enabled) return;
    // For multi-chain tokens, just update the network
    if (selectedToken?.isMultiChain && selectedToken.supportedChains?.includes(network.id)) {
      setSelectedNetwork(network);
      return;
    }
    // For single-chain tokens, try to switch the chain
    try {
      await switchChain(network.id);
      setSelectedNetwork(network);
    } catch (error) {
      console.error('Error switching network:', error);
      toast.error('Failed to switch network. Please try again.');
    }
  };

  const getDisplayAddress = () => {
    if (!selectedToken || !selectedNetwork) return '';
    return getTokenAddress(selectedToken, selectedNetwork);
  };

  const handleCopy = (text, type) => {
    if (!text) {
      toast.error('No address available to copy');
      return;
    }
    navigator.clipboard.writeText(text);
    setCopiedText(type);
    toast.success(`${type} copied to clipboard!`);
    setTimeout(() => setCopiedText(''), 2000);
  };

  const renderAddressSection = () => {
    if (isWalletLoading || isAddressLoading) {
      return (
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading address…</p>
        </div>
      );
    }

    const displayAddress = getDisplayAddress();

    if (!displayAddress) {
      return (
        <div className="error-message-mini" style={{ justifyContent: 'center', margin: '24px 0' }}>
          <FiAlertCircle style={{ marginRight: 6, fontSize: 18 }} />
          <span>No address available. Connect wallet.</span>
        </div>
      );
    }

    const isMultiChain = selectedToken?.isMultiChain;
    const supportedNetworks = getTokenSupportedNetworks(selectedToken);
    // Debug logging
    console.log('selectedToken', selectedToken);
    console.log('supportedNetworks', supportedNetworks);
    console.log('dropdown disabled?', !(selectedToken?.isMultiChain && supportedNetworks.length > 1));

    return (
      <>
        <div className="qr-container" style={{ marginBottom: 18, background: 'rgba(255,255,255,0.03)', borderRadius: 18, padding: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <QRCodeSVG
            value={displayAddress}
            size={180}
            bgColor="transparent"
            fgColor="currentColor"
            level="L"
            includeMargin={false}
          />
        </div>

        <div className="address-section">
          <div className="address-label">
            {selectedToken.isNative ? 'Wallet Address' : `${selectedToken.symbol} Token Address`}
          </div>
          <div className="address-container" style={{ minHeight: 44 }}>
            <div className="address-text" style={{ fontSize: '1.05rem', wordBreak: 'break-all' }}>
              {displayAddress}
            </div>
            <motion.button
              className="copy-button"
              onClick={() => handleCopy(displayAddress, 'Address')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ borderRadius: 8, fontSize: 18 }}
              tabIndex={0}
              aria-label="Copy address to clipboard"
            >
              {copiedText === 'Address' ? <FiCheck /> : <FiCopy />}
            </motion.button>
          </div>
        </div>

        <div className="network-section">
          <div className="network-label">Network</div>
          <NetworkSelector
            networks={NETWORKS}
            selectedNetwork={selectedNetwork}
            onSelect={handleNetworkSelect}
            disabled={!(selectedToken?.isMultiChain && supportedNetworks.length > 1)}
            availableNetworks={supportedNetworks}
          />
        </div>

        {!selectedToken.isNative && (
          <div className="warning-message" aria-live="polite" tabIndex={0}>
            <FiAlertCircle style={{ marginRight: 8, fontSize: 20 }} />
            <span>
              Only send {selectedToken.symbol} on {selectedNetwork?.name} to this address.
            </span>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="receive-page">
      <Header title="Receive" className="sticky-header" />
      
      <div className="receive-content">
        <motion.div 
          className="receive-card"
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <div className="token-selector">
            <TokenSelectButton
              token={selectedToken}
              onClick={() => setIsTokenSelectOpen(true)}
              disabled={isWalletLoading || isAddressLoading}
              size={48}
              tabIndex={0}
              aria-label="Select token to receive"
            />
            <TokenSelector
              isOpen={isTokenSelectOpen}
              onClose={() => setIsTokenSelectOpen(false)}
              onSelect={handleTokenSelect}
              selectedToken={selectedToken}
              tokens={availableTokens}
            />
          </div>
          <div className="qr-section">
            {renderAddressSection()}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ReceivePage;
